import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { AdminHeader, AdminSidebar } from "../components/AdminHeader";
import {
  LogIn,
  Plus,
  Trash2,
  Edit,
  Send,
  RefreshCw,
  FileText,
  Activity,
  User,
} from "lucide-react";

const ACTION_ICONS = {
  login: LogIn,
  create_project: Plus,
  delete_project: Trash2,
  create_user: Plus,
  update_user: Edit,
  delete_user: Trash2,
  resend_invitation: Send,
  reopen_project: RefreshCw,
};

const ACTION_LABELS = {
  login: "Anmeldung",
  create_project: "Projekt erstellt",
  delete_project: "Projekt gelöscht",
  create_user: "Benutzer erstellt",
  update_user: "Benutzer aktualisiert",
  delete_user: "Benutzer gelöscht",
  resend_invitation: "Einladung gesendet",
  reopen_project: "Projekt freigegeben",
};

const AdminActivityLog = () => {
  const { user, logout, api } = useAuth();
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState("all");
  const [selectedAction, setSelectedAction] = useState("all");
  const [selectedEntityType, setSelectedEntityType] = useState("all");
  const [actions, setActions] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [selectedUserId, selectedAction, selectedEntityType]);

  const fetchData = async () => {
    try {
      const [logsRes, usersRes, actionsRes, entityTypesRes] = await Promise.all([
        api.get("/admin/activity-logs"),
        api.get("/admin/users"),
        api.get("/admin/activity-logs/actions"),
        api.get("/admin/activity-logs/entity-types"),
      ]);
      setLogs(logsRes.data);
      setUsers(usersRes.data);
      setActions(actionsRes.data);
      setEntityTypes(entityTypesRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Aktivitäten");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedUserId !== "all") params.append("user_id", selectedUserId);
      if (selectedAction !== "all") params.append("action", selectedAction);
      if (selectedEntityType !== "all") params.append("entity_type", selectedEntityType);
      
      const queryString = params.toString();
      const response = await api.get(`/admin/activity-logs${queryString ? `?${queryString}` : ""}`);
      setLogs(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Aktivitäten");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getActionIcon = (action) => {
    const Icon = ACTION_ICONS[action] || FileText;
    return <Icon className="w-4 h-4" />;
  };

  const getActionBadge = (action) => {
    const label = ACTION_LABELS[action] || action;
    let colorClass = "bg-slate-50 text-slate-700 border-slate-200";
    
    if (action.includes("delete")) {
      colorClass = "bg-red-50 text-red-700 border-red-200";
    } else if (action.includes("create")) {
      colorClass = "bg-emerald-50 text-emerald-700 border-emerald-200";
    } else if (action === "login") {
      colorClass = "bg-blue-50 text-blue-700 border-blue-200";
    } else if (action.includes("update") || action.includes("reopen")) {
      colorClass = "bg-amber-50 text-amber-700 border-amber-200";
    }
    
    return (
      <Badge className={colorClass}>
        {getActionIcon(action)}
        <span className="ml-1">{label}</span>
      </Badge>
    );
  };

  const isSuperAdmin = user?.role === "superadmin";

  return (
    <div className="page-bg min-h-screen">
      <AdminSidebar />
      <div className="ml-56">
        <AdminHeader />

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="font-manrope font-bold text-2xl text-foreground">
                Aktivitätsprotokoll
              </h1>
              <p className="text-muted-foreground mt-1">
                Sehen Sie alle Benutzeraktivitäten im System
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">Filter:</span>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger className="w-[180px]" data-testid="filter-user">
                <SelectValue placeholder="Alle Benutzer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Benutzer</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedAction} onValueChange={setSelectedAction}>
              <SelectTrigger className="w-[160px]" data-testid="filter-action">
                <SelectValue placeholder="Alle Aktionen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Aktionen</SelectItem>
                {actions.map((a) => (
                  <SelectItem key={a} value={a}>
                    {ACTION_LABELS[a] || a}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedEntityType} onValueChange={setSelectedEntityType}>
              <SelectTrigger className="w-[140px]" data-testid="filter-entity">
                <SelectValue placeholder="Alle Bereiche" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Bereiche</SelectItem>
                {entityTypes.map((e) => (
                  <SelectItem key={e} value={e}>
                    {e.charAt(0).toUpperCase() + e.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Activity Log */}
        <Card className="card-base">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle className="font-manrope text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-slate-600" />
              Aktivitäten ({logs.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e3a5f]"></div>
              </div>
            ) : logs.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                Keine Aktivitäten gefunden
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-48">Zeitpunkt</TableHead>
                      <TableHead className="w-48">Benutzer</TableHead>
                      <TableHead className="w-40">Aktion</TableHead>
                      <TableHead>Details</TableHead>
                      <TableHead className="w-32">Bereich</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="font-mono text-sm text-slate-600">
                          {formatDate(log.timestamp)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-slate-600" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{log.user_name}</p>
                              <p className="text-xs text-slate-500">{log.user_email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{getActionBadge(log.action)}</TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {log.details}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {log.entity_type}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
      </div>
    </div>
  );
};

export default AdminActivityLog;
